import { useMemo } from 'react';
import { ArtistMediaCtxFragment, MediaTypeCodeEnum } from '@minecraft.graphql-operations';
import { gqlMediaTypeCodeEnum } from '@minecraft.graphql-types';

/**
 * Returns Infinity if the artist has unlimited entitlement, otherwise returns the entitlement count
 */
const getEntitlement = ({ mediaType, artist }: { artist: ArtistMediaCtxFragment; mediaType: MediaTypeCodeEnum }) => {
  if (![MediaTypeCodeEnum.Audio, MediaTypeCodeEnum.Video, MediaTypeCodeEnum.Photo].includes(mediaType)) {
    return 0;
  }

  // for a new account this will be 2 for photos, 1 for videos, 1 for audio
  // for premium accounts, their entitlement is -1 (no limit)
  const entitlement = artist?.subscriptionMediaUsage.entitle.find((e) => e.mediaType === mediaType) ?? {
    count: 0,
    isUnlimited: false,
  };

  return entitlement.isUnlimited ? Infinity : entitlement.count;
};

export type UseArtistEntitlementParams = {
  artist: ArtistMediaCtxFragment;
};

export type UseArtistEntitlement = {
  artistId?: ArtistMediaCtxFragment['artistId'];
  hasMediaCreditsAvailable?: boolean;
  mediaCreditsRemaining?: Record<string, number>;
  availableCreditsByType: Record<Exclude<gqlMediaTypeCodeEnum, 'DOCUMENT'>, number>;
  entitlementsByType: Record<Exclude<gqlMediaTypeCodeEnum, 'DOCUMENT'>, number>;
  hasCreditsAvailableByType: Record<Exclude<gqlMediaTypeCodeEnum, 'DOCUMENT'>, boolean>;
};

export const useArtistEntitlement = ({ artist: artistEntity }: UseArtistEntitlementParams): UseArtistEntitlement => {
  return useMemo(() => {
    // "available" in the subscriptionMediaUsage is the number of available credits the user has:
    const mediaCreditsRemaining = {
      audio:
        artistEntity?.subscriptionMediaUsage.available.find((e) => e.mediaType === MediaTypeCodeEnum.Audio)?.count ?? 0,
      photo:
        artistEntity?.subscriptionMediaUsage.available.find((e) => e.mediaType === MediaTypeCodeEnum.Photo)?.count ?? 0,
      video:
        artistEntity?.subscriptionMediaUsage.available.find((e) => e.mediaType === MediaTypeCodeEnum.Video)?.count ?? 0,
    };

    // Entitlements are the number of credits this user would have overall (typically based on plan)
    const entitlements = {
      audioUpload: getEntitlement({ artist: artistEntity, mediaType: MediaTypeCodeEnum.Audio }),
      photoUpload: getEntitlement({ artist: artistEntity, mediaType: MediaTypeCodeEnum.Photo }),
      videoUpload: getEntitlement({ artist: artistEntity, mediaType: MediaTypeCodeEnum.Video }),
    };

    // If there are ANY credits available in any media type:
    const hasMediaCreditsAvailable = Object.values(mediaCreditsRemaining).some((credits) => credits > 0);

    return {
      artistId: artistEntity?.artistId,
      hasMediaCreditsAvailable,
      mediaCreditsRemaining,
      availableCreditsByType: {
        [gqlMediaTypeCodeEnum.PHOTO]: mediaCreditsRemaining?.photo,
        [gqlMediaTypeCodeEnum.VIDEO]: mediaCreditsRemaining?.video,
        [gqlMediaTypeCodeEnum.AUDIO]: mediaCreditsRemaining?.audio,
      },
      entitlementsByType: {
        [gqlMediaTypeCodeEnum.PHOTO]: entitlements?.photoUpload,
        [gqlMediaTypeCodeEnum.VIDEO]: entitlements?.videoUpload,
        [gqlMediaTypeCodeEnum.AUDIO]: entitlements?.audioUpload,
      },
      hasCreditsAvailableByType: {
        [gqlMediaTypeCodeEnum.PHOTO]: mediaCreditsRemaining?.photo > 0,
        [gqlMediaTypeCodeEnum.VIDEO]: mediaCreditsRemaining?.video > 0,
        [gqlMediaTypeCodeEnum.AUDIO]: mediaCreditsRemaining?.audio > 0,
      },
    };
  }, [artistEntity]);
};
